import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { useMultipleZones } from '../hooks/useZoneStatus';
import { ZONE_IDS, updateZoneStatusSuccess } from '../store/slices/zonesSlice';
import { getZoneDisplayName } from '../utils/zoneUtils';
import TcButton from './TcButton';
import TcPaper from './TcPaper';

// Helper function to safely convert any value to a string
const getSafeErrorString = (error: any): string => {
    if (typeof error === 'string') {
        return error;
    }
    
    if (error && typeof error === 'object') {
        // Handle API error objects
        if (error.message && typeof error.message === 'string') {
            return error.message;
        }
        
        // Handle other error formats
        if (error.statusText && typeof error.statusText === 'string') {
            return error.statusText;
        }
        
        // Log warning for debugging
        console.warn('⚠️ ZoneAdmin received error object instead of string:', error);
        
        // Fallback for unknown error objects
        return error.toString ? error.toString() : 'An error occurred';
    }
    
    if (error === null || error === undefined) {
        return 'An error occurred';
    }
    
    // Convert any other type to string
    return String(error);
};

export default function ZoneAdmin() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isResetting, setIsResetting] = useState<Record<string, boolean>>({});
    
    // Get current user's visitorId from Redux (Frontend-only mode)
    const { visitorId: reduxVisitorId } = useAppSelector(state => state.user);
    const visitorId = reduxVisitorId || localStorage.getItem('visitorId') || undefined;

    // Debug visitor ID
    console.log('🔍 ZoneAdmin visitor ID state:', {
        reduxVisitorId,
        effectiveVisitorId: visitorId,
        fallbackUsed: visitorId !== reduxVisitorId,
        hasVisitorId: !!visitorId,
        visitorIdType: typeof visitorId,
        visitorIdLength: visitorId?.length
    });
    
    const allZoneIds = Object.values(ZONE_IDS);
    const { zonesData, isAnyLoading, hasAnyError, fetchAllZones } = useMultipleZones(allZoneIds);

    const handleResetZone = async (zoneId: string) => {
        setIsResetting(prev => ({ ...prev, [zoneId]: true }));
        try {
            console.log(`🔄 Resetting zone ${zoneId} (Frontend-only mode)`);
            
            // Use Redux-only zone reset
            dispatch(updateZoneStatusSuccess({
                zoneId,
                statusData: {
                    status: 'available',
                    claimedBy: undefined,
                    payload: { option: 0 },
                    zoneId: zoneId,
                    updatedAt: new Date().toISOString()
                },
                visitorId
            }));
            
            console.log(`✅ Zone ${zoneId} reset to available (Frontend-only mode)`);
            // Refresh the zone status
            setTimeout(() => {
                fetchAllZones();
            }, 500);
        } catch (error) {
            console.error(`❌ Failed to reset zone ${zoneId}:`, error);
        } finally {
            setIsResetting(prev => ({ ...prev, [zoneId]: false }));
        }
    };

    return (
        <div className="zone-admin p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white leading-tight">Zone Status Admin</h2>
                <TcButton
                    handleClick={fetchAllZones}
                    disabled={isAnyLoading}
                    classNameStyle="text-sm py-2 px-4 max-w-40 mr-0"
                    hollow={true}
                >
                    {isAnyLoading ? 'Refreshing...' : 'Refresh All'}
                </TcButton>
            </div>

            {isAnyLoading && (
                <div className="text-center py-4 text-yellow-300 mb-4">
                    Loading zones...
                </div>
            )}

            <div className="grid gap-4">
                {zonesData.map(({ zoneId, status, isLoading: zoneLoading, error: zoneError }) => {
                    // Ensure zoneError is always a string
                    const safeZoneError = zoneError ? getSafeErrorString(zoneError) : null;
                    
                    return (
                        <div key={zoneId} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-medium text-white">{getZoneDisplayName(zoneId)}</h3>
                                <div className="flex items-center gap-2">
                                    {zoneLoading && <span className="text-yellow-300 text-sm">Loading...</span>}
                                    {status && (
                                        <span className={`text-sm px-3 py-1 rounded-full ${
                                            status.status === 'available' ? 'bg-green-600 text-green-100' :
                                            status.status === 'processing' ? 'bg-blue-600 text-blue-100' :
                                            'bg-red-600 text-red-100'
                                        }`}>
                                            {status.status.toUpperCase()}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {safeZoneError && (
                                <div className="mb-3 p-2 bg-red-900/50 border border-red-500 rounded text-red-300 text-sm">
                                    Error: {safeZoneError}
                                </div>
                            )}

                            {status && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                                    <div className="text-left">
                                        <span className="text-gray-400">Zone ID:</span>
                                        <span className="text-white ml-2">{zoneId}</span>
                                    </div>
                                    <div className="text-left">
                                        <span className="text-gray-400">Status:</span>
                                        <span className="text-white ml-2">
                                            {status?.status || 'Unknown'}
                                        </span>
                                    </div>
                                    {status.claimedBy && (
                                        <div className="md:col-span-2 text-left">
                                            <span className="text-gray-400">Claimed By: </span>
                                            <br />
                                            <span className="text-orange-300">{status.claimedBy}</span>
                                        </div>
                                    )}
                                    {status.payload && (
                                        <div className="md:col-span-2 text-left">
                                            <span className="text-gray-400">Payload:</span>
                                            <pre className="text-white text-xs mt-1 bg-gray-900 p-2 rounded border max-h-32 overflow-auto">
                                                {JSON.stringify(status.payload, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <TcButton
                                    handleClick={() => handleResetZone(zoneId)}
                                    disabled={isResetting[zoneId] || zoneLoading}
                                    classNameStyle="text-sm py-1 px-3"
                                    hollow={true}
                                >
                                    {isResetting[zoneId] ? 'Resetting...' : 'Reset to Available'}
                                </TcButton>
                                
                                {status?.status === 'processing' && (
                                    <span className="text-blue-300 text-sm">
                                        ⚡ Zone is actively in use
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {hasAnyError && (
                <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
                    <span className="text-red-300">
                        ⚠️ Some zones failed to load. Check individual zone errors above.
                    </span>
                </div>
            )}

            <div className="mt-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                <h3 className="font-medium text-white mb-2">Zone Status Legend</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-600 rounded-full"></span>
                        <span className="text-green-300">Available - Ready for visitors</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-blue-600 rounded-full"></span>
                        <span className="text-blue-300">Processing - Visitor actively using</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-red-600 rounded-full"></span>
                        <span className="text-red-300">Error - Needs attention</span>
                    </div>
                </div>
            </div>
        </div>
    );
} 